import { CommonModule } from '@angular/common'
import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'

import { ResearchService } from '../../../../services/research.service'
import { Answer, Document, Feedback } from '../../../../types'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { StatisticsService } from '../../../../services/statistics.service'

@Component({
   selector: 'app-rating-dialog',
   standalone: true,
   imports: [CommonModule, ReactiveFormsModule, TranslocoModule],
   templateUrl: './rating-dialog.component.html',
   styles: ''
})
export class RatingDialogComponent implements OnChanges, OnInit {

   @Input() showModal = false
   @Input() type!: 'answer' | 'record'
   @Input() source!: Document
   @Input() feedback?: Feedback
   @Input() currentReaction: Feedback['reaction']
   @Input() answer?: Answer
   @Input() sourceId?: string

   @ViewChild('dialog') dialog!: ElementRef

   ratingText = ''
   feedbackGood: FormGroup
   feedbackBad: FormGroup

   constructor(
      private fb: FormBuilder,
      private stats: StatisticsService,
      private research: ResearchService,
      private translocoService: TranslocoService) {
      this.feedbackGood = this.createFeedbackFormGroup()
      this.feedbackBad = this.createFeedbackFormGroup()
   }

   ngOnInit() {
      // here we have to set the form values to the  possibly set @Input feedback value, in case this answer already has a feedback

      if (this.feedback) {
         if (this.feedback.reaction === 'thumbs_up') {
            this.feedbackGood = this.fb.group({
               comment: this.feedback.comment ?? '',
               reasons: this.fb.array(this.feedback.reasons ?? [])
            })
         }
         if (this.feedback.reaction === 'thumbs_down') {
            this.feedbackBad = this.fb.group({
               comment: this.feedback.comment ?? '',
               reasons: this.fb.array(this.feedback.reasons ?? [])
            })
         }
      }
   }

   get typeLocalization() {
      if (this.type === 'answer') {
         return this.translocoService.translate('rating.type.overview')
      }
      else if (this.type === 'record') {
         return this.translocoService.translate('rating.type.source')
      }
      return ''
   }

   get feedbackBadReasons() {
      return this.feedbackBad.get('reasons') as FormArray
   }

   get feedbackGoodReasons() {
      return this.feedbackGood.get('reasons') as FormArray
   }

   onCheckboxChange(event: Event, formArray: FormArray) {
      const checkbox = event.target as HTMLInputElement
      if (checkbox.checked) {
         formArray.push(new FormControl(checkbox.value))
      } else {
         const index = formArray.controls.findIndex(ctrl => ctrl.value === checkbox.value)
         if (index !== -1) {
            formArray.removeAt(index)
         }
      }
   }

   // toggle open/close on input boolean
   ngOnChanges(changes: SimpleChanges): void {
      if (this.dialog?.nativeElement && changes['feedback'] && typeof changes['feedback'].currentValue !== 'undefined') {
         // open if a new rating comes in, but if someone 'cancels' their rating (empty string) just ignore
         if (changes['feedback'].currentValue?.reaction === 'thumbs_down' || changes['feedback'].currentValue?.reaction === 'thumbs_up') {
            this.feedbackGood = this.createFeedbackFormGroup()
            this.feedbackBad = this.createFeedbackFormGroup()
            this.dialog.nativeElement.showModal()

         }
      }
   }

   cancel(){
      if(this.feedback){
         this.feedback.reaction = this.currentReaction ?? undefined
      }
      this.close()
   }

   close() {
      this.showModal = false
      this.dialog.nativeElement.close()
   }

   submit() {
      if (this.feedback && !this.isFormInvalid()) { // adding validation in case someone manually removes the 'disabled'
         if (this.feedback.reaction === 'thumbs_up') {
            this.feedback.comment = this.feedbackGood.get('comment')?.value ?? ''
            this.feedback.reasons = this.feedbackGood.get('reasons')?.value ?? []
         }
         if (this.feedback.reaction === 'thumbs_down') {
            this.feedback.comment = this.feedbackBad.get('comment')?.value ?? ''
            this.feedback.reasons = this.feedbackBad.get('reasons')?.value ?? []
         }

         this.feedback.answer = this.answer?.answer
         this.feedback.answerId = this.answer?.id
         this.feedback.sourceId = this.sourceId
         if(this.type === 'record'){
            this.stats.sendEvent({ category: 'feedback', action: this.feedback.reaction, label: 'sourceId', value: this.feedback.sourceId, question: this.feedback.query })
         }
         else {
            this.stats.sendEvent({ category: 'feedback', action: this.feedback.reaction, label: 'answerId', value: this.feedback.answerId, question: this.feedback.query })
         }
         this.research.sendFeedback(this.feedback)
      } else {
         console.warn('weird, there is no feedback?')
         this.cancel()
      }
      this.close()
   }

   isFormInvalid(): boolean {
      if (this.feedback?.reaction === 'thumbs_down') {
         return this.feedbackBad.invalid
      } else if (this.feedback?.reaction === 'thumbs_up') {
         return this.feedbackGood.invalid
      }
      return false
   }

   private createFeedbackFormGroup(): FormGroup {
      return this.fb.group({
         comment: '',
         reasons: this.fb.array([], Validators.required)
      })
   }

   // Define the checkbox options for different types
   feedbackGoodOptions = {
      answer: [
         { value: 'Answered my question/request', text: 'good.answered'},
         { value: 'Gave me accurate information', text: 'good.accurate'},
         { value: 'Sources met my expectations', text: 'good.expectations'},
         { value: 'Made my research task easier', text: 'good.easier'},
         { value: 'Other', text: 'other'},
      ],
      record: [
         { value: 'Relevant to my question', text: 'good.relevant'},
         { value: 'Gave me accurate information', text: 'good.accurate'},
         { value: 'Up to date', text: 'good.uptodate'},
         { value: 'I like the author(s), journal or publisher', text: 'good.like'},
         { value: 'Other', text: 'other'},
      ],
   }

   feedbackBadOptions = {
      answer: [
         { value: 'Didn’t answer my question/request', text: 'bad.didntanswer'},
         { value: 'Offensive, inappropriate or biased', text: 'bad.biased'},
         { value: 'Gave me inaccurate information', text: 'bad.inaccurate'},
         { value: 'Sources didn’t meet my expectations', text: 'bad.expectations'},
         { value: 'Other', text: 'other'},
      ],
      record: [
         { value: 'Not relevant to my question', text: 'bad.notrelevant'},
         { value: 'Gave me inaccurate information', text: 'bad.inaccurate'},
         { value: 'Out of date', text: 'bad.outofdate'},
         { value: 'Issue with the author(s), journal or publisher', text: 'bad.unlike'},
         { value: 'Other', text: 'other'},
      ],
   }
}
